import axios from "axios";
import store from "../store";
import router from "../router";
import Swal from "sweetalert2";
import CryptoJS from "crypto-js";
import { triggerVpnLogout } from "../utils/vpnCheck";

// Helper to recursively decrypt any fields starting with "ENC::"
function decryptData(obj) {
    if (!obj) return obj;
    if (typeof obj === 'string' && obj.startsWith('ENC::')) {
        try {
            const encryptedStr = obj.replace('ENC::', '');
            const secret = "HRMS_SECRET_KEY"; // Ensure this matches backend
            const bytes = CryptoJS.AES.decrypt(encryptedStr, secret);
            return bytes.toString(CryptoJS.enc.Utf8);
        } catch (e) {
            console.error("Decryption failed", e);
            return obj;
        }
    }
    if (Array.isArray(obj)) {
        return obj.map(item => decryptData(item));
    }
    if (typeof obj === 'object') {
        for (let key in obj) {
            if (obj.hasOwnProperty(key)) {
                obj[key] = decryptData(obj[key]);
            }
        }
    }
    return obj;
}
//const apibaseurl = "https://6fyr4aqbid.execute-api.ap-south-1.amazonaws.com/dev/";
 const apibaseurl = "";
// var apibaseurl = "https://qif3hy1uph.execute-api.ap-south-1.amazonaws.com/dev/";
//http://localhost:3000/dev/";
const instance = axios.create({
    baseURL: apibaseurl,
    timeout: 30000,
    headers: {
        "Content-Type": "application/json",
    },
});

// Request Interceptor: Automatically add Authorization header
instance.interceptors.request.use(
    (config) => {
        // Skip auth for public endpoints
        const publicEndpoints = ["loginUser", "sendOtp", "verifyOtp"];
        const isPublic = publicEndpoints.some(endpoint => config.url.includes(endpoint));

        const token = store.getters.authToken;

        // Proactive check: Validate token hasn't expired before making request
        if (token && !isPublic) {
            try {
                const base64Url = token.split('.')[1];
                const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
                const payload = JSON.parse(atob(base64));

                if (payload.exp) {
                    const currentTime = Math.floor(Date.now() / 1000);
                    if (payload.exp < currentTime) {
                        // Token expired - logout and reject request
                        console.warn('[Axios] Token has expired, logging out');
                        store.dispatch('logout');
                        router.push('/LoginView');
                        return Promise.reject(new Error('Token expired'));
                    }
                }
            } catch (error) {
                console.error('[Axios] Error validating token expiry:', error);
                // If we can't validate, proceed anyway
            }

            config.headers["Authorization"] = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response Interceptor: Handle global errors like 401/403
// NOTE: 401 errors are handled SILENTLY - re-login popup is controlled by useIdleTimer only
// This prevents security issues where someone blocks the popup or manipulates API responses
instance.interceptors.response.use(
    (response) => {
        if (response.data) {
            response.data = decryptData(response.data);
            
            // Check for VPN flag in successful responses (200 OK but with status: false, isVpn: true)
            if (response.data.isVpn || response.data.code === "VPN_DETECTED") {
                triggerVpnLogout("Backend Security Flag");
                return Promise.reject(new Error("VPN Detected"));
            }
        }
        return response;
    },
    async (error) => {
        const config = error.config;
        // Automatic 1-time retry for transient network errors / timeouts
        if (config && !config._retry && (!error.response || error.code === 'ECONNABORTED' || error.message === 'Network Error')) {
            config._retry = true;
            await new Promise(resolve => setTimeout(resolve, 1000));
            return instance(config);
        }

        // Only handle HTTP errors, not intentional thrown errors from components
        if (error.response) {
            const { status, data } = error.response;

            // Check if backend returned 403 with VPN flag
            if (status === 403 && (data?.isVpn || data?.code === "VPN_DETECTED")) {
                triggerVpnLogout("Backend Security Flag");
                return Promise.reject(error);
            }

            if (status === 401) {
                // Session expired/unauthorized: Silently logout
                // Re-login popup is controlled by idle timer (useIdleTimer), not by API responses
                // This prevents bypassing security by blocking popups or manipulating requests
                const currentToken = store.getters.authToken;
                if (currentToken) {
                    console.log('[Axios] 401 received - silently logging out. Re-login popup controlled by idle timer.');
                    store.dispatch("logout");
                    router.push("/LoginView");
                }
            } else if (status === 403) {
                // Permission denied: Show error but don't logout
                Swal.fire({
                    icon: "error",
                    title: "Permission Denied",
                    text: error.response.data?.message || "You don't have permission to perform this action.",
                });
            }
        }
        return Promise.reject(error);
    }
);

export default instance;
